/**
 * DisplayCoordinator - Level 1 Component
 *
 * RESPONSIBILITY:
 * Determines how to display results from multiple configurations and candidates.
 *
 * HIERARCHY:
 * - Reports to: ProjectOrchestrator
 * - Manages: Display logic and presentation strategies
 * - Peers: ConfigurationManager
 *
 * KEY OPERATIONS:
 * - getDisplayRecommendation: Determines optimal display mode for configurations
 * - selectBestCandidate: Identifies the best candidate for display
 * - formatComparison: Prepares data for comparison views
 *
 * NAVIGATION:
 * - For orchestration context, see: ./project-orchestrator.ts
 * - For UI components, see: ~/components/display/ConfigurationDisplay.tsx
 */

import type { BondSeries, Candidate, Configuration } from '~/types';
import { getConfigurationDisplayState } from '~/utils/configuration-status';

export interface DisplayRecommendation {
  primaryConfiguration: Configuration | null;
  comparisonConfigurations: Configuration[];
  displayMode: 'single' | 'comparison' | 'progression';
}

export interface CandidateDisplayInfo {
  candidate: Candidate;
  rank: number;
  metrics: {
    totalValue: number;
    efficiency: number;
    utilization: number;
    viability: boolean;
  };
}

export class DisplayCoordinator {
  /**
   * Determines the optimal way to display multiple configurations
   */
  getDisplayRecommendation(configurations: Configuration[]): DisplayRecommendation {
    if (!configurations || configurations.length === 0) {
      return {
        primaryConfiguration: null,
        comparisonConfigurations: [],
        displayMode: 'single',
      };
    }

    // Filter to only processed configurations
    const processedConfigurations = configurations.filter(
      (config) => config.candidates && config.candidates.length > 0
    );

    if (processedConfigurations.length === 0) {
      // No processed configurations, show the most recent one
      return {
        primaryConfiguration: configurations[configurations.length - 1],
        comparisonConfigurations: [],
        displayMode: 'single',
      };
    }

    // Single processed configuration
    if (processedConfigurations.length === 1) {
      return {
        primaryConfiguration: processedConfigurations[0],
        comparisonConfigurations: [],
        displayMode: 'single',
      };
    }

    // Multiple processed configurations - determine display mode
    const displayMode = this.determineDisplayMode(processedConfigurations);
    const primaryConfiguration = this.selectPrimaryConfiguration(processedConfigurations);
    const comparisonConfigurations = this.selectComparisonConfigurations(
      processedConfigurations,
      primaryConfiguration
    );

    return {
      primaryConfiguration,
      comparisonConfigurations,
      displayMode,
    };
  }

  /**
   * Determines the display mode based on configuration characteristics
   */
  private determineDisplayMode(
    configurations: Configuration[]
  ): 'single' | 'comparison' | 'progression' {
    // Check if configurations represent a progression (e.g., optimization stages)
    const isProgression = this.isConfigurationProgression(configurations);
    if (isProgression) {
      return 'progression';
    }

    // Check if configurations are for comparison (e.g., different setups)
    const isComparison = this.isConfigurationComparison(configurations);
    if (isComparison) {
      return 'comparison';
    }

    // Default to single display of best configuration
    return 'single';
  }

  /**
   * Checks if configurations represent a progression (same setup, different processing stages)
   */
  private isConfigurationProgression(configurations: Configuration[]): boolean {
    if (configurations.length < 2) {
      return false;
    }

    // Check if bond configurations are the same
    const firstBonds = configurations[0].bondSeries || [];
    for (let i = 1; i < configurations.length; i++) {
      const currentBonds = configurations[i].bondSeries || [];
      if (!this.areBondConfigurationsSame(firstBonds, currentBonds)) {
        return false;
      }
    }

    // Check if millage and DRF are the same
    for (let i = 1; i < configurations.length; i++) {
      if (!this.areInputsSame(configurations[0], configurations[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks if configurations are for comparison (different setups)
   */
  private isConfigurationComparison(configurations: Configuration[]): boolean {
    if (configurations.length < 2) {
      return false;
    }

    // If not a progression, it's a comparison
    return !this.isConfigurationProgression(configurations);
  }

  /**
   * Compares bond configurations between environments
   */
  private areBondConfigurationsSame(bonds1: BondSeries[], bonds2: BondSeries[]): boolean {
    if (bonds1.length !== bonds2.length) {
      return false;
    }

    for (let i = 0; i < bonds1.length; i++) {
      const bond1 = bonds1[i];
      const bond2 = bonds2[i];
      if (
        bond1.targetInformation?.principalTargetAmount !==
          bond2.targetInformation?.principalTargetAmount ||
        bond1.targetInformation?.targetType !== bond2.targetInformation?.targetType
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Compares input configurations between configurations
   */
  private areInputsSame(config1: Configuration, config2: Configuration): boolean {
    return (
      config1.millageInputs.peakMillage === config2.millageInputs.peakMillage &&
      config1.millageInputs.minimumMillage === config2.millageInputs.minimumMillage &&
      config1.drfInputs.startingBalance === config2.drfInputs.startingBalance &&
      config1.drfInputs.minimumBalance === config2.drfInputs.minimumBalance
    );
  }

  /**
   * Selects the primary configuration for display
   */
  private selectPrimaryConfiguration(configurations: Configuration[]): Configuration {
    // Prefer the most recent configuration with successful processing
    const successfulConfigurations = configurations.filter(
      (config) => config.candidates && config.candidates.length > 0
    );

    if (successfulConfigurations.length > 0) {
      return successfulConfigurations[successfulConfigurations.length - 1];
    }

    // Fallback to most recent configuration
    return configurations[configurations.length - 1];
  }

  /**
   * Selects configurations for comparison display
   */
  private selectComparisonConfigurations(
    configurations: Configuration[],
    primaryConfiguration: Configuration
  ): Configuration[] {
    // Exclude the primary configuration
    const otherConfigurations = configurations.filter(
      (config) => config.id !== primaryConfiguration.id
    );

    // Limit to last 2 configurations for comparison
    return otherConfigurations.slice(-2);
  }

  /**
   * Selects the best candidate from a configuration for display
   */
  selectBestCandidate(configuration: Configuration): Candidate | null {
    if (!configuration.candidates || configuration.candidates.length === 0) {
      return null;
    }

    // Filter to viable candidates
    const viableCandidates = configuration.candidates.filter((c) => c.results?.isViable || false);
    if (viableCandidates.length === 0) {
      // No viable candidates, return the first one for display
      return configuration.candidates[0];
    }

    // Sort by total value (descending) and return the best
    const sorted = [...viableCandidates].sort((a, b) => {
      const aValue = a.results?.totalPrincipal || 0;
      const bValue = b.results?.totalPrincipal || 0;
      return bValue - aValue;
    });

    return sorted[0];
  }

  /**
   * Ranks candidates within a configuration
   */
  rankCandidates(configuration: Configuration): CandidateDisplayInfo[] {
    if (!configuration.candidates || configuration.candidates.length === 0) {
      return [];
    }

    const displayInfos: CandidateDisplayInfo[] = configuration.candidates.map((candidate) => ({
      candidate,
      rank: 0,
      metrics: {
        totalValue: candidate.results?.totalPrincipal || 0,
        efficiency: this.calculateEfficiency(candidate),
        utilization: this.calculateUtilization(candidate),
        viability: candidate.results?.isViable || false,
      },
    }));

    // Sort by viability first, then by total value
    displayInfos.sort((a, b) => {
      // Viable candidates come first
      if (a.metrics.viability !== b.metrics.viability) {
        return a.metrics.viability ? -1 : 1;
      }
      // Then sort by total value
      return b.metrics.totalValue - a.metrics.totalValue;
    });

    // Assign ranks
    displayInfos.forEach((info, index) => {
      info.rank = index + 1;
    });

    return displayInfos;
  }

  /**
   * Calculates efficiency metric for a candidate
   * Efficiency already calculated correctly in candidate.results
   * Returns as decimal (0-1) for consistency
   */
  private calculateEfficiency(candidate: Candidate): number {
    // Efficiency already calculated correctly in candidate.results
    // Return as decimal (0-1) for consistency
    return candidate.results?.efficiency || 0;
  }

  /**
   * Calculates utilization metric for a candidate
   */
  private calculateUtilization(candidate: Candidate): number {
    if (!candidate.results?.totalRevenue || !candidate.results?.totalPrincipal) {
      return 0;
    }
    return (candidate.results.totalRevenue / candidate.results.totalPrincipal) * 100;
  }

  /**
   * Formats comparison data for display
   */
  formatComparison(configurations: Configuration[]): {
    headers: string[];
    rows: Array<{
      label: string;
      values: (string | number)[];
    }>;
  } {
    const headers = configurations.map((config) => `Configuration ${config.orderId}`);

    const rows = [
      {
        label: 'Total Candidates',
        values: configurations.map((config) => config.totalCandidates || 0),
      },
      {
        label: 'Viable Candidates',
        values: configurations.map(
          (config) => config.candidates?.filter((c) => c.results?.isViable).length || 0
        ),
      },
      {
        label: 'Best Value',
        values: configurations.map((config) => {
          const best = this.selectBestCandidate(config);
          return best?.results?.totalPrincipal
            ? `$${(best.results.totalPrincipal / 1000000).toFixed(2)}M`
            : 'N/A';
        }),
      },
      {
        label: 'Processing Status',
        values: configurations.map((config) => getConfigurationDisplayState(config)),
      },
    ];

    return { headers, rows };
  }
}
